/**
 * Scoped HTTP client for API requests.
 * Replaces the global fetch override with a dedicated client that:
 * - Injects Bearer token from session
 * - Maps LOCAL_API_URL to API_URL
 * - Handles 401/403/404 with toast (no global loading, no button disabling)
 */

import config from '../../config'
import { toast } from 'react-toastify'
import { RefreshTokenRequest, TokensClient } from './WebApiClient'

const AUTH_SESSION_KEY = 'portal_user'
const REFRESH_PATH = '/api/tokens/refresh'

let refreshTokenPromise: Promise<string | null> | null = null

const getStoredSession = (): any | null => {
	const user = sessionStorage.getItem(AUTH_SESSION_KEY)
	if (!user) return null
	try {
		return JSON.parse(user)
	} catch {
		return null
	}
}

const getToken = (): string => {
	return getStoredSession()?.token ?? ''
}

const clearSessionAndRedirectToLogin = () => {
	sessionStorage.removeItem(AUTH_SESSION_KEY)
	if (window.location.pathname !== '/auth/login') {
		window.location.href = '/auth/login'
	}
}

const createRefreshFetch = (apiUrl: string, localApiUrl: string): ((url: RequestInfo, init?: RequestInit) => Promise<Response>) => {
	return (url: RequestInfo, init?: RequestInit): Promise<Response> => {
		const urlString = typeof url === 'string' ? url : url.toString()
		const modifiedUrl = localApiUrl ? urlString.replace(localApiUrl, apiUrl) : urlString
		return window.fetch(modifiedUrl, init)
	}
}

const refreshAccessToken = async (baseUrl: string, apiUrl: string, localApiUrl: string): Promise<string | null> => {
	const session = getStoredSession()
	if (!session?.token || !session?.refreshToken) {
		clearSessionAndRedirectToLogin()
		return null
	}

	try {
		const tokensClient = new TokensClient(baseUrl, { fetch: createRefreshFetch(apiUrl, localApiUrl) })
		const request = new RefreshTokenRequest({
			token: session.token,
			refreshToken: session.refreshToken,
		})
		const refreshedSession = await tokensClient.refresh(request)
		sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(refreshedSession))
		return refreshedSession?.token ?? null
	} catch {
		clearSessionAndRedirectToLogin()
		return null
	}
}

const handleErrorResponse = async (response: Response): Promise<never> => {
	let errorData: unknown
	try {
		errorData = await response.clone().json()
	} catch {
		errorData = await response.text()
	}

	switch (response.status) {
		case 401:
			toast.error('Invalid credentials or session expired')
			break
		case 403:
			window.location.href = '/access-denied'
			break
		case 404:
			toast.error('Sorry! The data you are looking for could not be found')
			break
		default:
			// Other errors - let caller handle via messageHelper in catch
			break
	}

	return Promise.reject(errorData)
}

/**
 * Creates an authenticated fetch function for use with NSwag clients.
 * Only applies to requests - no global loading or button disabling.
 */
export const createAuthenticatedFetch = (): ((url: RequestInfo, init?: RequestInit) => Promise<Response>) => {
	const apiUrl = config.API_URL ?? ''
	const localApiUrl = config.LOCAL_API_URL ?? ''
	const baseUrl = localApiUrl || apiUrl || 'https://localhost:7027'

	return async (url: RequestInfo, init?: RequestInit): Promise<Response> => {
		const token = getToken()
		const urlString = typeof url === 'string' ? url : url.toString()
		const modifiedUrl = localApiUrl ? urlString.replace(localApiUrl, apiUrl) : urlString

		const modifiedInit: RequestInit = {
			...init,
			headers: {
				'Content-Type': 'application/json',
				...(init?.headers || {}),
				Authorization: token ? `Bearer ${token}` : '',
			},
		}

		const response = await window.fetch(modifiedUrl, modifiedInit)

		if (response.ok) {
			return response
		}

		if (response.status === 401 && !modifiedUrl.toLowerCase().includes(REFRESH_PATH)) {
			refreshTokenPromise ??= refreshAccessToken(baseUrl, apiUrl, localApiUrl).finally(() => {
				refreshTokenPromise = null
			})

			const refreshedToken = await refreshTokenPromise
			if (refreshedToken) {
				const retryResponse = await window.fetch(modifiedUrl, {
					...modifiedInit,
					headers: {
						...modifiedInit.headers,
						Authorization: `Bearer ${refreshedToken}`,
					},
				})

				if (retryResponse.ok) {
					return retryResponse
				}

				return handleErrorResponse(retryResponse)
			}
		}

		return handleErrorResponse(response)
	}
}

/** Singleton authenticated fetch for API clients */
export const authenticatedFetch = createAuthenticatedFetch()
